import React, { useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { emailService } from '../../services/emailService';
import type { EmailSettings } from '../../types/email';

interface Props {
  settings: EmailSettings;
  onClose: () => void;
  onSaved: (next: EmailSettings) => void;
}

/** The name families see an email come from, and where their replies go. Empty means the school's own. */
const SenderDetailsDialog: React.FC<Props> = ({ settings, onClose, onSaved }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [fromName, setFromName] = useState(settings.fromName);
  const [replyTo, setReplyTo] = useState(settings.replyTo);
  const [saving, setSaving] = useState(false);

  const dirty = fromName.trim() !== settings.fromName || replyTo.trim().toLowerCase() !== settings.replyTo;

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!dirty || saving) return;
    setSaving(true);
    try {
      onSaved(await emailService.updateSettings({ fromName: fromName.trim(), replyTo: replyTo.trim() }));
      enqueueSnackbar('Saved', { variant: 'success' });
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Couldn’t save', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={() => !saving && onClose()} maxWidth="xs" fullWidth>
      <form onSubmit={save} noValidate>
        <DialogTitle component="div" sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight={700}>Sender name and replies</Typography>
          <Typography variant="caption" color="text.secondary">Leave a field empty to use your school’s own.</Typography>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <TextField
              label="Sender name" value={fromName} onChange={e => setFromName(e.target.value)}
              placeholder={settings.defaults.fromName} InputLabelProps={{ shrink: true }} inputProps={{ maxLength: 80 }}
              helperText="Who families see the email is from." fullWidth autoComplete="off"
            />
            <TextField
              label="Replies go to" type="email" value={replyTo} onChange={e => setReplyTo(e.target.value)}
              placeholder={settings.defaults.replyTo || 'Your school’s email'} InputLabelProps={{ shrink: true }}
              helperText="Where a parent’s reply lands." fullWidth autoComplete="off"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} color="inherit" disabled={saving}>Cancel</Button>
          <Button type="submit" variant="contained" disableElevation disabled={!dirty || saving}>{saving ? 'Saving…' : 'Save'}</Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default SenderDetailsDialog;
